/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { TenantManagementState } from 'app/page-modules/tenant-management/store/tenant-management.state';
import * as tenantManagementApiAction from './tenant-management-api.actions';
import { withData, withError, withLoading } from 'app/shared/store/utils/loadable-state';
import { defaultLoadableState } from '@opengamma/ui';

export function reducer(
  state: TenantManagementState,
  action: tenantManagementApiAction.All
): TenantManagementState {
  switch (action.type) {
    // -------------------------------------------------------------------------
    case tenantManagementApiAction.CREATE_TENANT_DETAILS:
      return {
        ...state,
        tenantCreationInProgress: true
      };

    case tenantManagementApiAction.CREATE_TENANT_DETAILS_SUCCESS:
      return {
        ...state,
        tenantCreationInProgress: false,
        tenantCreationWizardShown: false
      };

    case tenantManagementApiAction.CREATE_TENANT_DETAILS_ERROR:
      return {
        ...state,
        tenantCreationInProgress: false
      };

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.LOAD_TENANT_DETAILS:
      return {
        ...state,
        selectedTenant: {
          loading: true,
          error: undefined,
          data: undefined
        },
        usersForSelectedTenant: {
          loading: true,
          error: undefined,
          data: undefined
        }
      };

    case tenantManagementApiAction.LOAD_TENANT_DETAILS_SUCCESS:
      return {
        ...state,
        selectedTenant: {
          loading: false,
          error: undefined,
          data: {
            ...state.selectedTenant.data,
            ...action.tenantDetailsWithFlags.data
          }
        },
        selectedTenantEditable: action.tenantDetailsWithFlags.editable,
        selectedTenantBiEditable: action.tenantDetailsWithFlags.editableBi
      };

    case tenantManagementApiAction.LOAD_TENANT_DETAILS_ERROR:
      return {
        ...state,
        selectedTenant: {
          ...state.selectedTenant,
          loading: false,
          error: action.errorMessage
        },
        selectedTenantEditable: false,
        selectedTenantBiEditable: false
      };

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.UPDATE_TENANT_DETAILS:
      return {
        ...state,
        tenantAttributeBeingUpdated: action.attribute
      };

    case tenantManagementApiAction.UPDATE_TENANT_DETAILS_SUCCESS:
      return {
        ...state,
        tenantAttributeBeingUpdated: 'none',
        selectedTenant: {
          ...state.selectedTenant,
          data: {
            // data must be present
            ...state.selectedTenant.data!,
            ...action.updateRequest.tenantDetails
          }
        }
      };

    case tenantManagementApiAction.UPDATE_TENANT_DETAILS_ERROR:
      return {
        ...state,
        tenantAttributeBeingUpdated: 'none'
      };

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.LOAD_USERS_FOR_TENANT:
      return withLoading(state, 'usersForSelectedTenant');

    case tenantManagementApiAction.LOAD_USERS_FOR_TENANT_SUCCESS:
      return withData(state, 'usersForSelectedTenant', action.users);

    case tenantManagementApiAction.LOAD_USERS_FOR_TENANT_ERROR:
      return withError(state, 'usersForSelectedTenant', action.errorMessage);

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.ADD_USER_FOR_TENANT:
      return withLoading(state, 'newUser');

    case tenantManagementApiAction.ADD_USER_FOR_TENANT_SUCCESS:
      // usersForSelectedTenant.data must be present in this action
      const adUserData = [...state.usersForSelectedTenant.data!, action.user];
      return {
        ...state,
        newUser: defaultLoadableState(),
        usersForSelectedTenant: {
          loading: false,
          error: undefined,
          data: adUserData
        }
      };

    case tenantManagementApiAction.ADD_USER_FOR_TENANT_ERROR:
      return withError(state, 'newUser', action.errorMessage, true);

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.UPDATE_NEW_USER_DETAILS:
      return {
        ...state,
        newUser: {
          ...state.newUser,
          data: action.user
        }
      };

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.UPDATE_USER_FOR_TENANT_SUCCESS:
      return {
        ...state,
        usersForSelectedTenant: {
          // data must be present in this action
          data: state.usersForSelectedTenant
            .data!.filter(({ email }) => email !== action.user.email)
            .concat(action.user),
          error: undefined,
          loading: false
        }
      };

    // -------------------------------------------------------------------------
    case tenantManagementApiAction.DELETE_USER_FOR_TENANT:
      return withLoading(state, 'usersForSelectedTenant');

    case tenantManagementApiAction.DELETE_USER_FOR_TENANT_SUCCESS:
      // data must be present for deletion action to be dispatched
      const deleteUserData = state.usersForSelectedTenant.data!.filter(
        ({ email }) => email !== action.user.email
      );
      return withData(state, 'usersForSelectedTenant', deleteUserData);

    case tenantManagementApiAction.DELETE_USER_FOR_TENANT_ERROR:
      return {
        ...state,
        usersForSelectedTenant: {
          ...state.usersForSelectedTenant,
          error: action.errorMessage,
          loading: false
        }
      };
  }

  return state;
}
